import * as React from 'react';
import {
  Alert,
  Button,
  PageSection,
  Progress,
  Spinner,
  Title,
} from '@patternfly/react-core';
import {
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@patternfly/react-table';
import { useLocation } from 'react-router-dom';
import { useDocumentTitle } from '@app/utils/useDocumentTitle';
import { API_BASE_URL } from '../../config/bootstrap';
import { buildTimeBranding, useSiteConfig } from '../config/SiteConfigContext';
import { getPortalBearerToken } from '@app/utils/portalAuth';

interface StatusRow {
  timestamp: number;
  collector: string;
  status: string;
}

const POLL_INTERVAL_MS = 5000;
const AUTO_REFRESH_DURATION_MS = 15 * 60 * 1000;

const isTerminalStatus = (status: string): boolean => {
  const value = status.trim().toLowerCase();
  return value.endsWith('collection complete') || value.includes('collection failed');
};

const mergeStatusRows = (existing: StatusRow[], incoming: StatusRow[]): StatusRow[] => {
  if (incoming.length === 0) {
    return existing;
  }
  const seen = new Set(existing.map((row) => row.timestamp));
  const appended = incoming.filter((row) => !seen.has(row.timestamp));
  if (appended.length === 0) {
    return existing;
  }
  return [...existing, ...appended].sort((a, b) => a.timestamp - b.timestamp);
};

const formatTimestamp = (timestamp: number): string => {
  if (!Number.isFinite(timestamp)) {
    return '-';
  }

  // Support both seconds and milliseconds since epoch
  const millis = timestamp < 1e12 ? timestamp * 1000 : timestamp;
  const date = new Date(millis);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }
  return date.toLocaleString();
};

const CheckStatus: React.FunctionComponent = () => {
  const site = useSiteConfig();
  useDocumentTitle(
    `${site?.siteId || buildTimeBranding.siteId || 'QuickCheck'} ${site?.productName || buildTimeBranding.applicationName || ''} | Status`
  );

  const location = useLocation();
  const searchParams = React.useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const requestId = searchParams.get('requestId');

  const [rows, setRows] = React.useState<StatusRow[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string | null>(null);
  const [timeToNextPoll, setTimeToNextPoll] =
    React.useState<number>(POLL_INTERVAL_MS);
  const [autoRefreshRemainingMs, setAutoRefreshRemainingMs] =
    React.useState<number>(AUTO_REFRESH_DURATION_MS);
  const [autoRefreshPaused, setAutoRefreshPaused] =
    React.useState<boolean>(false);
  const [refreshToken, setRefreshToken] = React.useState<number>(0);
  const rowsRef = React.useRef<StatusRow[]>([]);
  const newestTimestampRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    rowsRef.current = [];
    newestTimestampRef.current = null;
    setRows([]);
    setError(null);
    setLoading(Boolean(requestId));
    setAutoRefreshPaused(false);
    setAutoRefreshRemainingMs(AUTO_REFRESH_DURATION_MS);
    setTimeToNextPoll(POLL_INTERVAL_MS);
  }, [requestId]);

  React.useEffect(() => {
    if (!requestId) {
      setLoading(false);
      return;
    }

    if (autoRefreshPaused) {
      return;
    }

    let cancelled = false;
    let stopPolling = false;
    let pollTimeout: number | undefined;

    const fetchStatus = async () => {
      try {
        setError(null);

        const idToken = await getPortalBearerToken();
        if (!idToken) {
          throw new Error('No ID token available for status request');
        }

        const since = newestTimestampRef.current;
        const params = new URLSearchParams({ requestId });
        if (since != null) {
          params.set('since', String(since));
        }

        const response = await fetch(
          `${API_BASE_URL}/collect/status?${params.toString()}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${idToken}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Status request failed: ${response.status}`);
        }

        const data = await response.json();
        const items = Array.isArray(data.items) ? data.items : [];

        const mapped: StatusRow[] = items
          .map((item: any) => ({
            timestamp: Number(item.Timestamp),
            collector: String(item.Collector ?? ''),
            status: String(item.Status ?? ''),
          }))
          .filter((row) => Number.isFinite(row.timestamp))
          .sort((a, b) => a.timestamp - b.timestamp);

        if (!cancelled) {
          const merged = mergeStatusRows(
            since == null ? [] : rowsRef.current,
            mapped
          );
          rowsRef.current = merged;
          if (merged.length > 0) {
            newestTimestampRef.current = merged[merged.length - 1].timestamp;
          }
          setRows(merged);
          setLoading(false);
          setTimeToNextPoll(POLL_INTERVAL_MS);
          const latest = merged.length > 0 ? merged[merged.length - 1].status : '';
          if (isTerminalStatus(latest)) {
            stopPolling = true;
            setAutoRefreshPaused(true);
          }
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(
            err?.message || 'Unable to load collection status at this time.'
          );
          setLoading(false);
          setTimeToNextPoll(POLL_INTERVAL_MS);
        }
      } finally {
        if (!cancelled && !autoRefreshPaused && !stopPolling) {
          pollTimeout = window.setTimeout(fetchStatus, POLL_INTERVAL_MS);
        }
      }
    };

    if (rowsRef.current.length === 0) {
      setLoading(true);
    }
    fetchStatus();

    return () => {
      cancelled = true;
      if (pollTimeout !== undefined) {
        window.clearTimeout(pollTimeout);
      }
    };
  }, [requestId, autoRefreshPaused, refreshToken]);

  React.useEffect(() => {
    if (!requestId || autoRefreshPaused) {
      return;
    }

    let intervalId: number | undefined;

    intervalId = window.setInterval(() => {
      setTimeToNextPoll((prev) => {
        const next = prev - 200;
        return next <= 0 ? 0 : next;
      });

      setAutoRefreshRemainingMs((prev) => {
        const next = prev - 200;
        if (next <= 0) {
          setAutoRefreshPaused(true);
          return 0;
        }
        return next;
      });
    }, 200);

    return () => {
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
      }
    };
  }, [requestId, autoRefreshPaused]);

  const handleManualRefresh = () => {
    if (!requestId) {
      return;
    }
    setAutoRefreshRemainingMs(AUTO_REFRESH_DURATION_MS);
    setTimeToNextPoll(POLL_INTERVAL_MS);
    setAutoRefreshPaused(false);
    setRefreshToken((prev) => prev + 1);
  };

  const progressValue =
    100 - (timeToNextPoll / POLL_INTERVAL_MS) * 100;
  const remainingSeconds = timeToNextPoll / 1000;

  return (
    <PageSection isFilled>
      <Title headingLevel="h1" size="xl">
        Check Status
      </Title>

      {!requestId && (
        <Alert
          variant="warning"
          title="No request ID supplied"
          isInline
        >
          The status page requires a requestId query parameter in the
          URL in order to load collection status.
        </Alert>
      )}

      {requestId && error && (
        <Alert
          variant="danger"
          title="Unable to load status"
          isInline
        >
          {error}
        </Alert>
      )}

      {requestId && loading && (
        <div style={{ marginTop: '1rem' }}>
          <Spinner size="md" />
        </div>
      )}

      {requestId && !loading && rows.length === 0 && !error && (
        <Alert
          variant="info"
          title="No status records yet"
          isInline
        >
          The collection has started but no status updates are
          available yet. This page will update automatically.
        </Alert>
      )}

      {requestId && rows.length > 0 && (
        <div style={{ marginTop: '1rem' }}>
          <Table
            aria-label="Collection status"
            variant="compact"
            borders={false}
          >
            <Thead>
              <Tr>
                <Th>Timestamp</Th>
                <Th>Collector</Th>
                <Th>Status</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((row, index) => (
                <Tr key={`${row.timestamp}-${index}`}>
                  <Td dataLabel="Timestamp">
                    {formatTimestamp(row.timestamp)}
                  </Td>
                  <Td dataLabel="Collector">{row.collector}</Td>
                  <Td dataLabel="Status">{row.status}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </div>
      )}

      {requestId && (
        <div style={{ marginTop: '1rem', maxWidth: '400px' }}>
          <Progress
            value={Math.max(0, Math.min(100, progressValue))}
            min={0}
            max={100}
            measureLocation="none"
            aria-label="Time until the next status refresh"
          />
        </div>
      )}

      {requestId && autoRefreshPaused && (
        <div style={{ marginTop: '0.75rem' }}>
          <Button variant="secondary" onClick={handleManualRefresh}>
            Refresh now
          </Button>
        </div>
      )}
    </PageSection>
  );
};

export { CheckStatus };

